<template>
  <main class="cover-auth">
    <div class="widget-sign-up">
      <form class="s20 form" @submit.prevent="handleSubmit">
        <a href="#">
          <div class="text-center mb-8">
            <img src="/public/images/logo-wide.svg" class="auth-logo2" />
          </div>
        </a>

        <h2 class="font-semibold mt-5 mb-4 text-center">Définir votre quotité cessible</h2>
        <p class="text-read text-grey-600 text-center pb-5">
          La quotité cessible est le montant maximum que vous pouvez utiliser mensuellement.
        </p>

        <!-- Message d'erreur -->
        <div v-if="errorMessage" class="alert alert-danger mb-5">
          <p class="text-sm text-center">{{ errorMessage }}</p>
        </div>

        <div class="s10_mobile container-full-2">
          <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
            <img src="/public/icons/icon-engagement.svg" class="size-l" alt="" />
            Quotité cessible
          </h2>

          <div class="form-field-floating xl reactive active my-5">
            <input
              type="text"
              class="form-input"
              name="salaire_brut"
              id="salaire_brut"
              v-model="formData.salaireBrut"
              @input="calculateQuotiteCessible"
              placeholder=""
              readonly
            >
            <label for="salaire_brut">Salaire brut <b class="color-danger">*</b></label>
            <span>F&nbsp;CFA</span>
          </div>

          <div class="form-field-split active my-5">
            <div class="form-field-floating xl reactive half">
              <input
                type="text"
                class="form-input"
                name="quotite_cessible"
                id="quotite_cessible"
                v-model="formData.quotiteCessible"
                @input="calculateQuotiteCessibleValue"
                placeholder=""
              >
              <label for="quotite_cessible">Quotité cessible max <b class="color-danger">*</b></label>
              <span class="">%</span>
            </div>
            <hr class="hr" />
            <div class="form-field-floating xl reactive half">
              <input
                type="text"
                class="form-input"
                name="quotite_cessible_val"
                id="quotite_cessible_val"
                v-model="formData.quotiteCessibleVal"
                readonly
                placeholder=""
              >
              <label for="quotite_cessible_val">Valeur <b class="color-danger">*</b></label>
              <span class="">F&nbsp;CFA</span>
            </div>
          </div>

          <button
            type="submit"
            class="btn lg w-100 flex-center mt-8 mb-7"
            :disabled="isSubmitting"
          >
            <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
            <span>{{ isSubmitting ? 'Enregistrement en cours...' : 'Continuer vers le KYC' }}</span>
          </button>
        </div>
      </form>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { API_CONFIG } from '@/services/apiConfig'
import { showAlert } from '@/utils/swal'

interface FormData {
  salaireBrut: string
  quotiteCessible: string
  quotiteCessibleVal: string
}

export default defineComponent({
  name: 'SignUpQuotite',
  setup() {
    const router = useRouter()
    const isSubmitting = ref(false)
    const errorMessage = ref('')
    const userId = ref<number | null>(null)

    const formData = reactive<FormData>({
      salaireBrut: '',
      quotiteCessible: '',
      quotiteCessibleVal: ''
    })

    onMounted(async () => {
      // Récupérer l'ID utilisateur et le salaire depuis sessionStorage
      const storedUserId = sessionStorage.getItem('invitation_user_id')
      const storedSalaire = sessionStorage.getItem('invitation_user_salaire')

      if (!storedUserId) {
        await showAlert.error('Session expirée. Veuillez recommencer le processus d\'inscription.')
        router.push('/auth/sign-in')
        return
      }

      userId.value = parseInt(storedUserId)
      formData.salaireBrut = storedSalaire || ''
    })

    const calculateQuotiteCessible = () => {
      if (formData.salaireBrut && formData.quotiteCessible) {
        calculateQuotiteCessibleValue()
      }
    }

    const calculateQuotiteCessibleValue = () => {
      if (formData.salaireBrut && formData.quotiteCessible) {
        const salaire = parseFloat(formData.salaireBrut.replace(/\s/g, ''))
        const quotite = parseFloat(formData.quotiteCessible)

        if (!isNaN(salaire) && !isNaN(quotite)) {
          const value = (salaire * quotite) / 100
          formData.quotiteCessibleVal = Math.round(value).toString()
        }
      }
    }

    const handleSubmit = async () => {
      // Validation
      if (!formData.quotiteCessible || !formData.quotiteCessibleVal) {
        errorMessage.value = 'Veuillez renseigner la quotité cessible'
        return
      }

      const quotitePourcent = parseFloat(formData.quotiteCessible)
      if (quotitePourcent < 0 || quotitePourcent > 100) {
        errorMessage.value = 'La quotité cessible doit être entre 0 et 100%'
        return
      }

      isSubmitting.value = true
      errorMessage.value = ''

      try {
        // Mettre à jour la quotité cessible de l'utilisateur
        const response = await axios.post(
          `${API_CONFIG.baseURL}/users/${userId.value}/update-quotite`,
          {
            quotiteCessiblePourcent: parseFloat(formData.quotiteCessible),
            quotiteCessibleMontant: parseFloat(formData.quotiteCessibleVal.replace(/\s/g, ''))
          },
          {
            headers: API_CONFIG.headers
          }
        )

        if (response.data.status === 0) {
          // await showAlert.success('Quotité cessible enregistrée avec succès')

          // Rediriger vers le KYC
          router.push('/auth/sign-up-kyc')
        } else {
          errorMessage.value = response.data.message || 'Erreur lors de l\'enregistrement'
        }
      } catch (error: any) {
        console.error('Erreur:', error)
        errorMessage.value = error.response?.data?.message || 'Une erreur est survenue'
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      formData,
      isSubmitting,
      errorMessage,
      calculateQuotiteCessible,
      calculateQuotiteCessibleValue,
      handleSubmit
    }
  }
})
</script>

<style scoped>
.alert {
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.alert-danger {
  background-color: #fee;
  border: 1px solid #fcc;
  color: #c33;
}
</style>
